import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  TextField,
  InputAdornment,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AppDialog from "../../General/Dialog/AppDialog";
import {
  getLiquidationItemTypes,
  syncDependencyItemTypes,
  getDependencyItemTypes,
} from "../../../services/LiquidationService";
import { useTheme } from "@mui/material/styles";

const CALC_LABELS = {
  jus_multiplier:       "Mult. JUS",
  percentage_of_amount: "Porcentaje",
  fixed_catalog:        "Catálogo fijo",
  interest_on_tax:      "Interés s/tasa",
  manual:               "Manual",
};

export default function DependencyItemTypesDialog({ open, onClose, dependency, onSaved }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError]           = useState("");
  const [saveError, setSaveError]   = useState("");

  useEffect(() => {
    if (!open || !dependency) return;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [typesRes, cfgRes] = await Promise.all([
          getLiquidationItemTypes({ perPage: 200, page: 1, search: "" }),
          getDependencyItemTypes(dependency.id),
        ]);

        const types = typesRes.data?.data ?? [];
        const configured = cfgRes.data?.item_types ?? [];

        const configMap = {};
        configured.forEach((t) => {
          configMap[t.id] = t.pivot ?? {};
        });

        setRows(
          types.map((t) => ({
            id:               t.id,
            name:             t.name,
            key:              t.key,
            category:         t.category,
            calculation_type: t.calculation_type,
            enabled:          configMap[t.id]?.is_enabled ?? false,
            is_default:       configMap[t.id]?.is_default ?? false,
            multiplier:       configMap[t.id]?.multiplier ?? "",
            sort_order:       configMap[t.id]?.sort_order ?? t.sort_order ?? 0,
          }))
        );
      } catch {
        setError("No se pudo cargar la configuración.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, dependency]);

  const update = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError("");
    try {
      const itemTypes = rows
        .filter((r) => r.enabled)
        .map((r) => ({
          id:         r.id,
          multiplier: r.multiplier !== "" ? parseFloat(r.multiplier) : null,
          is_enabled: r.enabled,
          sort_order: parseInt(r.sort_order, 10) || 0,
        }));

      await syncDependencyItemTypes(dependency.id, { item_types: itemTypes });
      onSaved?.();
      onClose();
    } catch (e) {
      setSaveError(e?.response?.data?.message ?? "Error al guardar la configuración.");
    } finally {
      setSaveLoading(false);
    }
  };

  const CATEGORY_COLORS = { capital: "#1976d2", honorarios: "#2e7d32", gastos: "#e65100" };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={`Configurar rubros — ${dependency?.name ?? ""}`}
      subtitle="Activá los rubros y configurá el multiplicador JUS por juzgado"
      maxWidth="md"
      fullWidth
      stickyHeader
      actions={
        <>
          <Button onClick={onClose} disabled={saveLoading}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
            onClick={handleSave}
            disabled={saveLoading || loading}
            sx={{ borderRadius: 2, fontWeight: 900 }}
          >
            Guardar
          </Button>
        </>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {error    && <Alert severity="error">{error}</Alert>}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <TableContainer
            sx={{
              borderRadius: 2,
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.09)",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}>
                  <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Activo</TableCell>
                  <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Rubro</TableCell>
                  <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Categoría</TableCell>
                  <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Tipo cálculo</TableCell>
                  <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Multiplicador JUS</TableCell>
                  <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Orden</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  const isJus    = row.calculation_type === "jus_multiplier";
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        opacity: row.enabled ? 1 : 0.45,
                        "&:nth-of-type(even)": {
                          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={row.enabled}
                          onChange={(e) => update(idx, "enabled", e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{row.name}</TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 900,
                            color: CATEGORY_COLORS[row.category] ?? "text.secondary",
                            textTransform: "uppercase",
                            fontSize: 10,
                          }}
                        >
                          {row.category}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontSize: 11, opacity: 0.75 }}>
                          {CALC_LABELS[row.calculation_type] ?? row.calculation_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isJus ? (
                          <TextField
                            size="small"
                            value={row.multiplier}
                            onChange={(e) => update(idx, "multiplier", e.target.value)}
                            disabled={!row.enabled}
                            type="number"
                            sx={{ width: 100 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">×</InputAdornment>,
                            }}
                            placeholder="—"
                          />
                        ) : (
                          <Typography variant="caption" sx={{ opacity: 0.35, fontSize: 11 }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.sort_order}
                          onChange={(e) => update(idx, "sort_order", e.target.value)}
                          disabled={!row.enabled}
                          type="number"
                          sx={{ width: 70 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </AppDialog>
  );
}
